import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import { isRouteType, ROUTE_TYPES } from "../../shared/stepConfig";
import { useQuery } from "../hooks/useQuery";
import type { ParsedStep } from "../lib/parseCommitData";
import {
  ROUTE_TYPE_DESCRIPTIONS,
  ROUTE_TYPE_ICONS,
  ROUTE_TYPE_INLINE_LABELS,
  ROUTE_TYPE_LABELS,
} from "../lib/routeTypeDisplay";
import { makeUrl, routes, useCurrentUrl } from "../lib/urls";
import { EmptyState } from "./EmptyState";
import { LinkFilterFooter, LinkFilterIconButton } from "./LinkFilterFooter";
import { PageCard } from "./PageCard";
import { Text } from "./Text";

type Props = {
  workflowId: string;
  branchId: string;
  steps: ReadonlyArray<ParsedStep>;
  onStepHover?: (canonicalId: string | null) => void;
};

const Scroll = styled("div")`
  flex: 1;
  min-height: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;

const Outer = styled("div")`
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
`;

const Grid = styled("div")`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 24px;
  padding: 32px 32px 0;
  box-sizing: border-box;
  align-content: start;
  align-items: start;

  &::after {
    content: "";
    grid-column: 1 / -1;
    height: 8px;
  }
`;

const EmptyFilterArea = styled("div")`
  flex: 1;
  min-height: 0;
`;

const PlaceholderCard = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const PlaceholderFrame = styled("div")`
  position: relative;
  height: 180px;
  background: var(--alpha4);
  border-radius: 16px;
  padding: 24px 24px 0 24px;
  overflow: hidden;
`;

const PlaceholderPreview = styled("div")`
  position: relative;
  width: 100%;
  height: 100%;
  background: var(--alpha8);
  border-radius: 12px 12px 0 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px;
  text-align: center;
`;

const GhostCard = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const GhostFrame = styled("div")`
  height: 180px;
  background: var(--alpha4);
  border-radius: 16px;
  padding: 24px 24px 0 24px;
  overflow: hidden;
`;

const GhostThumbnail = styled("div")`
  width: 100%;
  height: 100%;
  background: var(--alpha8);
  border-radius: 12px 12px 0 0;
`;

const GhostMetaWrap = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0 12px;
`;

const GhostMetaLine = styled("div")`
  background: var(--alpha4);
  border-radius: 6px;

  &[data-size="title"] {
    height: 16px;
    width: 40%;
  }
  &[data-size="subtitle"] {
    height: 12px;
    width: 65%;
  }
`;

const GHOST_LINK_KEYS = ["g0", "g1", "g2", "g3"];

export function WorkflowLinkGallery({
  workflowId,
  branchId,
  steps,
  onStepHover,
}: Props) {
  const stepColorByDir = new Map(steps.map((s) => [s.id, s.color]));
  const pinnedByDir = new Map(steps.map((s) => [s.id, s.pinned]));
  const z = useZero();
  const [workflow, query] = useQuery(
    queries.workflowPages({ workflowId, branchId })
  );
  const [routeFavoriteIds] = useQuery(queries.userFavoriteRouteIds());
  const { linkType } = useCurrentUrl(routes.space.workflow);
  const routeFavoriteSet = new Set(
    routeFavoriteIds.map((f) => `${f.workflowId}:${f.stepDir}`)
  );
  const isComplete = query.type === "complete";
  const typeFilter = isRouteType(linkType) ? linkType : null;

  const branch = workflow?.branches.find((b) => b.id === branchId);
  const space = workflow?.space;
  const commitRoutes = (branch?.headCommit?.routes ?? []).filter((r) =>
    isRouteType(r.routeType)
  );
  const sorted = [...commitRoutes].sort((a, b) => {
    const aPinned = pinnedByDir.get(a.stepDir) ?? false;
    const bPinned = pinnedByDir.get(b.stepDir) ?? false;
    if (aPinned !== bPinned) return aPinned ? -1 : 1;
    return a.route.localeCompare(b.route);
  });
  const filtered = typeFilter
    ? sorted.filter((r) => r.routeType === typeFilter)
    : sorted;

  if (!workflow || !branch || !space || commitRoutes.length === 0) {
    return isComplete ? (
      <Scroll>
        <Outer>
          <Grid>
            <PlaceholderCard>
              <PlaceholderFrame>
                <PlaceholderPreview>
                  <Text variant="text-small" color="var(--text-secondary)">
                    If this workflow has links,
                    <br />
                    they’ll appear here.
                  </Text>
                </PlaceholderPreview>
              </PlaceholderFrame>
              <GhostMetaWrap>
                <GhostMetaLine data-size="title" />
                <GhostMetaLine data-size="subtitle" />
              </GhostMetaWrap>
            </PlaceholderCard>
            {GHOST_LINK_KEYS.map((k) => (
              <GhostCard key={k}>
                <GhostFrame>
                  <GhostThumbnail />
                </GhostFrame>
                <GhostMetaWrap>
                  <GhostMetaLine data-size="title" />
                  <GhostMetaLine data-size="subtitle" />
                </GhostMetaWrap>
              </GhostCard>
            ))}
          </Grid>
        </Outer>
      </Scroll>
    ) : null;
  }

  return (
    <Scroll>
      <Outer>
        {typeFilter && filtered.length === 0 ? (
          <EmptyFilterArea>
            <EmptyState
              title={`No ${ROUTE_TYPE_INLINE_LABELS[typeFilter]} links`}
              description={ROUTE_TYPE_DESCRIPTIONS[typeFilter]}
              iconName={ROUTE_TYPE_ICONS[typeFilter]}
            />
          </EmptyFilterArea>
        ) : (
          <Grid>
            {filtered.map((route) => (
              <PageCard
                key={`${branch.id}-${route.route}`}
                workflow={workflow}
                branch={branch}
                route={route}
                space={space}
                showSpaceColumn={false}
                isFavorite={routeFavoriteSet.has(
                  `${workflow.id}:${route.stepDir}`
                )}
                onToggleFavorite={() =>
                  z.mutate(
                    sharedMutators.toggleRouteFavorite({
                      workflowId: workflow.id,
                      stepDir: route.stepDir,
                    })
                  )
                }
                title={route.stepDir}
                color={stepColorByDir.get(route.stepDir)}
                isPinned={pinnedByDir.get(route.stepDir)}
                showOpenInBrowserAction
                showBranchStatus={false}
                onHoverChange={(hovered) =>
                  onStepHover?.(hovered ? route.stepDir : null)
                }
              />
            ))}
          </Grid>
        )}
        <LinkFilterFooter>
          <LinkFilterIconButton
            to={makeUrl(routes.space.workflow, { linkType: "all" })}
            selected={!typeFilter}
            icon="layers"
            label="All"
          />
          {ROUTE_TYPES.map((t) => (
            <LinkFilterIconButton
              key={t}
              to={makeUrl(routes.space.workflow, {
                linkType: typeFilter === t ? "all" : t,
              })}
              selected={typeFilter === t}
              icon={ROUTE_TYPE_ICONS[t]}
              label={ROUTE_TYPE_LABELS[t]}
            />
          ))}
        </LinkFilterFooter>
      </Outer>
    </Scroll>
  );
}
